import { useEffect, useRef, useState } from 'react'
import { updateRoute, getApplicationsRoute } from '../../route'
import { AvailableAgent, Agent } from '../'
import { WizardConfigName, WizardStep } from '../wizard-config'

export const segment = 'wizard'

export interface ChildProps {
  isValid: boolean
  configName: WizardConfigName
  step: WizardStep
  updateWizardRoute: (configName: WizardConfigName, step: WizardStep) => void
  goToPreviousRoute: () => void
  goToApplicationsRoute: () => void
  getInitialRoute: (available: AvailableAgent[]) => string
}

interface UseAddAgentRouteParams {
  organizationID: string
  defaultStep: WizardStep
  defaultConfigName: WizardConfigName
  getRouteHistory: () => string[]
}

interface State {
  isValid: boolean
  step: WizardStep
  configName: WizardConfigName
}

/**
 * This hook is responsible for routing related functionality for the add agent wizard
 *  * Parsing the current route to get the agent and configuration step specified
 *  * Checking if the current route is valid for the wizard
 *  * Child component requests to update the route
 *  * Child component requests to navigate to the previous non-wizard route
 *  * Determining what the initial route will be (this is the route that the "add agent" button will
 *    be assigned in it's href attribute
 */
export const useAddAgentRoute = ({
  organizationID,
  getRouteHistory,
  defaultStep,
  defaultConfigName
}: UseAddAgentRouteParams) => {
  const [state, setState] = useState<State>({
    isValid: false,
    step: defaultStep,
    configName: defaultConfigName
  })

  const buildRoutePrefix = () => `#/${organizationID}/${segment}`

  // Track a route value so that we can update the route
  // after state has changed, to avoid updating state after
  // this hook has unmounted
  const routeRef = useRef<null | string>(null)

  const goToPreviousRoute = () => {
    setState({ ...state, isValid: false })
    routeRef.current = determinePreviousRoute(organizationID, getRouteHistory())
  }

  const goToApplicationsRoute = () => {
    setState({ ...state, isValid: false })
    routeRef.current = getApplicationsRoute(organizationID)
  }

  useEffect(() => {
    if (!state.isValid && routeRef.current !== null) {
      updateRoute(routeRef.current)
      routeRef.current = null
    }
  }, [state.isValid])

  const validate = () => {
    const isValid = window.location.hash.indexOf(`/${segment}`) !== -1
    if (isValid) {
      if (
        window.location.hash.indexOf(segment) + segment.length ===
        window.location.hash.length
      ) {
        // Base wizard route detected, navigate to default route
        window.location.hash = `${buildRoutePrefix()}/${defaultStep}/${defaultConfigName}`
        setState({
          isValid: true,
          configName: defaultConfigName,
          step: defaultStep
        })
      } else {
        // Parse the current route and update state if necessar
        const { step, configName } = parseRoute(
          window.location.hash,
          defaultStep,
          defaultConfigName
        )
        if (!configName) {
          throw new Error(
            `useAddAgentRoute failed to parse route in validate function`
          )
        }
        if (
          step !== state.step ||
          configName !== state.configName ||
          isValid !== state.isValid
        ) {
          setState({
            isValid,
            step,
            configName
          })
        }
      }
    } else if (isValid !== state.isValid) {
      setState({ ...state, isValid })
    }
  }

  const onHashChange = () => {
    validate()
  }

  const updateWizardRoute = (
    configName: WizardConfigName,
    step: WizardStep
  ) => {
    const route = `${buildRoutePrefix()}/${step}/${configName}`
    if (route !== window.location.hash) {
      updateRoute(route)
    }
  }

  useEffect(() => {
    validate()
    window.addEventListener('hashchange', onHashChange)
    return () => window.removeEventListener('hashchange', onHashChange)
  })

  return {
    ...state,
    updateWizardRoute,
    goToPreviousRoute,
    goToApplicationsRoute,
    getInitialRoute: (available: AvailableAgent[]) =>
      getInitialRoute(
        buildRoutePrefix(),
        available,
        defaultConfigName,
        defaultStep
      )
  }
}

export function getInitialRoute(
  prefix: string,
  available: Array<{ type: Agent }>,
  defaultConfigName: WizardConfigName,
  defaultStep: WizardStep
) {
  let initialAgent = defaultConfigName

  const defaultFound = available.some(({ type }) => type === defaultConfigName)
  if (!defaultFound && available.length) {
    initialAgent = available[0].type as WizardConfigName
  }

  return `${prefix}/${defaultStep}/${initialAgent}`
}

export function determinePreviousRoute(
  organizationID: string,
  routeHistory: string[]
) {
  const defaultRoute = `#/${organizationID}/dashboard`

  const previousRoute = [...routeHistory]
    .reverse()
    .find(
      (r) =>
        r.indexOf(segment) < 0 &&
        r.indexOf('/pages/signin') < 0 &&
        r.indexOf('/pages/404') < 0
    )

  return previousRoute ? previousRoute : defaultRoute
}

type ParseRouteFn = (
  route: string,
  defaultStep: WizardStep,
  defaultConfigName: WizardConfigName
) => {
  step: WizardStep
  agent?: WizardConfigName
  configName?: WizardConfigName
}

export const parseRoute: ParseRouteFn = (
  route,
  defaultStep,
  defaultConfigName
) => {
  //  remove query parameters first, then split into route navigation tokens
  const pieces = route.split('?')[0].split('/')
  if (pieces.length !== 5) {
    return { step: defaultStep, agent: defaultConfigName }
  }
  const step = pieces[3] as WizardStep
  const configName = pieces[4] as WizardConfigName
  return { step, configName }
}
